class YuanProductCarousel extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';
    this.cacheElements();
    this.currentProduct = this.readSingleProduct('[data-current-product]');
    window.YuanWishlist?.register(this.currentProduct);
    this.storeCurrentProduct();
    const products = this.productsForSource();
    if (products.length === 0) return;
    this.renderProducts(products);
    this.hidden = false;
    this.bindEvents();
    requestAnimationFrame(() => this.rebuildCarousel());
  }

  disconnectedCallback() {
    this.stopAutoplay();
    this.resizeObserver?.disconnect();
    document.removeEventListener('visibilitychange', this.visibilityHandler);
    document.removeEventListener('yuan:wishlist-change', this.wishlistHandler);
    clearTimeout(this.resizeTimer);
  }

  cacheElements() {
    this.track = this.querySelector('[data-carousel-track]');
    this.viewport = this.querySelector('[data-carousel-viewport]');
    this.cardTemplate = this.querySelector('[data-carousel-card-template]');
    this.status = this.querySelector('[data-carousel-status]');
    this.toggleButton = this.querySelector('[data-carousel-toggle]');
    this.autoplayEnabled = this.dataset.autoplay === 'true';
    this.autoplayUserPaused = false;
  }

  bindEvents() {
    this.addEventListener('click', this.onClick.bind(this));
    this.track.addEventListener('transitionend', this.onTransitionEnd.bind(this));
    this.viewport.addEventListener('keydown', this.onKeydown.bind(this));
    this.viewport.addEventListener('pointerdown', this.onPointerDown.bind(this));
    this.viewport.addEventListener('pointerup', this.onPointerUp.bind(this));
    this.addEventListener('mouseenter', () => this.stopAutoplay());
    this.addEventListener('mouseleave', () => this.startAutoplay());
    this.addEventListener('focusin', () => this.stopAutoplay());
    this.addEventListener('focusout', (event) => this.onFocusOut(event));
    this.visibilityHandler = this.onVisibilityChange.bind(this);
    document.addEventListener('visibilitychange', this.visibilityHandler);
    this.wishlistHandler = this.syncWishlistButtons.bind(this);
    document.addEventListener('yuan:wishlist-change', this.wishlistHandler);
    this.resizeObserver = new ResizeObserver(() => this.scheduleRebuild());
    this.resizeObserver.observe(this.viewport);
  }

  onClick(event) {
    const addButton = event.target.closest('[data-card-add]');
    if (addButton) return this.addProduct(addButton);
    const wishlistButton = event.target.closest('[data-card-wishlist]');
    if (wishlistButton) return this.toggleWishlist(wishlistButton);
    if (event.target.closest('[data-carousel-previous]')) return this.moveFromUser(-1);
    if (event.target.closest('[data-carousel-next]')) return this.moveFromUser(1);
    if (event.target.closest('[data-carousel-toggle]')) this.toggleAutoplay();
  }

  onKeydown(event) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    this.moveFromUser(event.key === 'ArrowLeft' ? -1 : 1);
  }

  onPointerDown(event) {
    if (event.target.closest('[data-card-wishlist], [data-card-add], [data-carousel-previous], [data-carousel-next], [data-carousel-toggle]')) {
      this.pointerStartX = NaN;
      return;
    }
    this.pointerStartX = event.clientX;
    this.stopAutoplay();
  }

  onPointerUp(event) {
    if (!Number.isFinite(this.pointerStartX)) return;
    const distance = event.clientX - this.pointerStartX;
    this.pointerStartX = null;
    if (Math.abs(distance) >= 40) this.moveFromUser(distance < 0 ? 1 : -1);
    this.startAutoplay();
  }

  onFocusOut(event) {
    if (!this.contains(event.relatedTarget)) this.startAutoplay();
  }

  onVisibilityChange() {
    if (document.hidden) this.stopAutoplay();
    else this.startAutoplay();
  }

  readSingleProduct(selector) {
    const products = this.parseProductJson(this.querySelector(selector)?.textContent || 'null');
    return this.normalizeProduct(products);
  }

  readProductList(selector) {
    const products = this.parseProductJson(this.querySelector(selector)?.textContent || '[]');
    if (!Array.isArray(products)) return [];
    return products.map((product) => this.normalizeProduct(product)).filter(Boolean);
  }

  parseProductJson(json) {
    try {
      return JSON.parse(json);
    } catch (parseError) {
      if (parseError instanceof SyntaxError) return null;
      throw parseError;
    }
  }

  normalizeProduct(product) {
    if (!product || typeof product !== 'object') return null;
    const id = Number(product.id);
    const variantId = Number(product.variantId);
    if (!Number.isFinite(id) || typeof product.title !== 'string' || typeof product.url !== 'string') return null;
    const vendor = typeof product.vendor === 'string' ? product.vendor : '';
    const productType = typeof product.productType === 'string' ? product.productType : '';
    const imageAlt = typeof product.imageAlt === 'string' ? product.imageAlt : product.title;
    return {
      id,
      title: product.title,
      url: product.url,
      vendor: this.containsArabicText(vendor) ? '' : vendor,
      productType: this.containsArabicText(productType) ? '' : productType,
      description: typeof product.description === 'string' ? product.description : '',
      imageUrl: typeof product.imageUrl === 'string' ? product.imageUrl : '',
      imageAlt: this.containsArabicText(imageAlt) ? product.title : imageAlt,
      price: typeof product.price === 'string' ? product.price : '',
      compareAtPrice: typeof product.compareAtPrice === 'string' ? product.compareAtPrice : '',
      variantId: Number.isFinite(variantId) ? variantId : null,
      available: product.available === true,
      hasOnlyDefaultVariant: product.hasOnlyDefaultVariant === true,
    };
  }

  containsArabicText(text) {
    return /[\u0600-\u06FF]/.test(text);
  }

  productsForSource() {
    const products = this.dataset.source === 'recently_viewed'
      ? this.readHistory().filter((product) => product.id !== this.currentProduct?.id)
      : this.readProductList('[data-carousel-products]');
    let limitedProducts = products.slice(0, Number(this.dataset.limit) || 8);
    if (limitedProducts.length === 0 && this.dataset.designMode === 'true') {
      limitedProducts = this.demoProducts();
    }
    return this.getAttribute('dir') === 'rtl' ? limitedProducts.reverse() : limitedProducts;
  }

  demoProducts() {
    const productCount = Math.min(Number(this.dataset.limit) || 8, 8);
    return Array.from({ length: productCount }, (_, index) => ({
      id: -(index + 1),
      title: `${this.dataset.demoTitle} ${index + 1}`,
      url: this.currentProduct?.url || '/',
      vendor: 'YUAN',
      productType: this.dataset.demoType,
      imageUrl: '',
      imageAlt: `${this.dataset.demoTitle} ${index + 1}`,
      price: this.dataset.demoPrice,
      compareAtPrice: '',
      variantId: null,
      available: true,
      hasOnlyDefaultVariant: false,
    }));
  }

  readHistory() {
    try {
      const savedProducts = JSON.parse(localStorage.getItem('yuan-recently-viewed-v1') || '[]');
      if (!Array.isArray(savedProducts)) return [];
      return savedProducts
        .map((product) => this.normalizeProduct(product))
        .filter((product) => product && !this.containsArabicText(product.title));
    } catch (storageError) {
      if (storageError instanceof DOMException || storageError instanceof SyntaxError) return [];
      throw storageError;
    }
  }

  storeCurrentProduct() {
    if (!this.currentProduct) return;
    const history = this.readHistory().filter((product) => product.id !== this.currentProduct.id);
    try {
      localStorage.setItem('yuan-recently-viewed-v1', JSON.stringify([this.currentProduct, ...history].slice(0, 20)));
    } catch (storageError) {
      if (!(storageError instanceof DOMException)) throw storageError;
    }
  }

  renderProducts(products) {
    this.productsById = new Map(products.map((product) => [product.id, product]));
    const fragment = document.createDocumentFragment();
    products.forEach((product, index) => fragment.append(this.createSlide(product, index, products.length)));
    this.track.replaceChildren(fragment);
  }

  createSlide(product, index, productCount) {
    const slide = this.cardTemplate.content.firstElementChild.cloneNode(true);
    const card = slide.querySelector('.yuan-carousel-card');
    card.dataset.productId = String(product.id);
    slide.setAttribute('aria-label', `${index + 1} / ${productCount}`);
    this.fillMedia(card, product);
    this.fillProductDetails(card, product);
    this.fillActions(card, product);
    return slide;
  }

  fillMedia(card, product) {
    const productUrl = this.safeProductUrl(product.url);
    const mediaLink = card.querySelector('[data-card-media]');
    const media = card.querySelector('.yuan-carousel-card__media');
    const image = card.querySelector('[data-card-image]');
    mediaLink.href = productUrl;
    const imageUrl = this.safeImageUrl(product.imageUrl);
    if (imageUrl) image.src = imageUrl;
    else media.classList.add('is-empty');
    image.alt = product.imageAlt;
    const badge = card.querySelector('[data-card-badge]');
    if (!product.available) this.showBadge(badge, this.dataset.soldOutLabel);
    else if (product.compareAtPrice) this.showBadge(badge, this.dataset.saleLabel);
  }

  showBadge(badge, label) {
    badge.textContent = label;
    badge.hidden = false;
  }

  fillProductDetails(card, product) {
    const productUrl = this.safeProductUrl(product.url);
    const meta = card.querySelector('[data-card-meta]');
    const metaText = product.productType || product.vendor;
    if (metaText) {
      meta.textContent = metaText;
      meta.hidden = false;
    }
    const title = card.querySelector('[data-card-title]');
    title.href = productUrl;
    title.textContent = product.title;
    card.querySelector('[data-card-price]').textContent = product.price;
    const comparePrice = card.querySelector('[data-card-compare]');
    if (product.compareAtPrice) {
      comparePrice.textContent = product.compareAtPrice;
      comparePrice.hidden = false;
    }
  }

  fillActions(card, product) {
    window.YuanWishlist?.register(product);
    const addButton = card.querySelector('[data-card-add]');
    const selectLink = card.querySelector('[data-card-select]');
    const addLabel = addButton.querySelector('span');
    if (!product.available) {
      addLabel.textContent = this.dataset.soldOutLabel;
      addButton.disabled = true;
      addButton.hidden = false;
    } else if (product.hasOnlyDefaultVariant && product.variantId) {
      addLabel.textContent = this.dataset.addLabel;
      addButton.dataset.variantId = String(product.variantId);
      addButton.hidden = false;
    } else {
      selectLink.href = this.safeProductUrl(product.url);
      selectLink.textContent = this.dataset.chooseLabel;
      selectLink.hidden = false;
    }
    this.setWishlistButton(card.querySelector('[data-card-wishlist]'), this.readWishlist(product.id));
  }

  safeProductUrl(url) {
    try {
      const productUrl = new URL(url, window.location.origin);
      if (productUrl.origin !== window.location.origin || !/(^|\/)products\//.test(productUrl.pathname)) return '/';
      return `${productUrl.pathname}${productUrl.search}`;
    } catch (urlError) {
      if (urlError instanceof TypeError) return '/';
      throw urlError;
    }
  }

  safeImageUrl(url) {
    if (!url) return '';
    try {
      const imageUrl = new URL(url, window.location.origin);
      return imageUrl.protocol === 'https:' ? imageUrl.href : '';
    } catch (urlError) {
      if (urlError instanceof TypeError) return '';
      throw urlError;
    }
  }

  readWishlist(productId) {
    return window.YuanWishlist?.has(productId) === true;
  }

  toggleWishlist(button) {
    const productId = Number(button.closest('.yuan-carousel-card').dataset.productId);
    const product = this.productsById?.get(productId);
    if (!product || !window.YuanWishlist) return;
    window.YuanWishlist.toggle(product);
  }

  syncWishlistButtons() {
    this.querySelectorAll('.yuan-carousel-card[data-product-id]').forEach((card) => {
      const productId = Number(card.dataset.productId);
      this.setWishlistButton(card.querySelector('[data-card-wishlist]'), this.readWishlist(productId));
    });
  }

  setWishlistButton(button, isActive) {
    button.setAttribute('aria-pressed', String(isActive));
    button.setAttribute('aria-label', isActive ? this.dataset.wishlistRemove : this.dataset.wishlistAdd);
  }

  async addProduct(button) {
    if (button.disabled) return;
    const buttonLabel = button.querySelector('span');
    const originalLabel = buttonLabel.textContent;
    button.disabled = true;
    button.classList.add('is-loading');
    try {
      const cartSurface = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      const cartResponse = await this.requestCartAdd(Number(button.dataset.variantId), cartSurface);
      this.showCartResponse(cartResponse, cartSurface);
      buttonLabel.textContent = this.dataset.addedLabel;
    } catch (cartError) {
      this.setStatus(cartError.message || this.dataset.errorLabel);
    } finally {
      button.classList.remove('is-loading');
      window.setTimeout(() => {
        button.disabled = false;
        buttonLabel.textContent = originalLabel;
      }, 1600);
    }
  }

  async requestCartAdd(variantId, cartSurface) {
    const requestBody = { items: [{ id: variantId, quantity: 1 }] };
    requestBody.sections = cartSurface
      ? cartSurface.getSectionsToRender().map((section) => section.id)
      : ['cart-icon-bubble'];
    requestBody.sections_url = window.location.pathname;
    cartSurface?.setActiveElement(document.activeElement);
    const response = await fetch(routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(requestBody),
    });
    const cartResponse = await response.json();
    if (!response.ok || cartResponse.status) {
      throw new Error(cartResponse.description || cartResponse.message || this.dataset.errorLabel);
    }
    return cartResponse;
  }

  showCartResponse(cartResponse, cartSurface) {
    if (cartSurface) {
      cartSurface.classList.remove('is-empty');
      cartSurface.renderContents(cartResponse);
    } else {
      this.updateCartIcon(cartResponse.sections['cart-icon-bubble']);
    }
    this.setStatus(this.dataset.addedLabel);
  }

  updateCartIcon(sectionHtml) {
    const cartIcon = document.getElementById('cart-icon-bubble');
    const sectionRoot = new DOMParser().parseFromString(sectionHtml, 'text/html').querySelector('.shopify-section');
    if (!cartIcon || !sectionRoot) return window.location.assign(routes.cart_url);
    cartIcon.innerHTML = sectionRoot.innerHTML;
  }

  setStatus(message) {
    this.status.textContent = '';
    requestAnimationFrame(() => { this.status.textContent = message; });
  }

  scheduleRebuild() {
    clearTimeout(this.resizeTimer);
    this.resizeTimer = window.setTimeout(() => this.rebuildCarousel(), 120);
  }

  rebuildCarousel() {
    this.stopAutoplay();
    this.removeClones();
    const originalSlides = [...this.track.children];
    const visibleSlides = this.visibleSlides();
    if (originalSlides.length <= visibleSlides) return this.disableCarousel();
    this.originalSlideCount = originalSlides.length;
    this.cloneCount = Math.min(visibleSlides, originalSlides.length);
    this.addClones(originalSlides);
    this.currentIndex = this.cloneCount;
    this.setAttribute('data-carousel-active', '');
    this.jumpToCurrentIndex();
    this.startAutoplay();
  }

  removeClones() {
    this.track.querySelectorAll('[data-carousel-clone]').forEach((clone) => clone.remove());
  }

  disableCarousel() {
    this.removeAttribute('data-carousel-active');
    this.currentIndex = 0;
    this.track.style.transform = 'translate3d(0, 0, 0)';
  }

  addClones(originalSlides) {
    const leadingSlides = originalSlides.slice(-this.cloneCount).map((slide) => this.prepareClone(slide));
    leadingSlides.reverse().forEach((slide) => this.track.prepend(slide));
    originalSlides.slice(0, this.cloneCount).forEach((slide) => this.track.append(this.prepareClone(slide)));
  }

  prepareClone(slide) {
    const clone = slide.cloneNode(true);
    clone.dataset.carouselClone = 'true';
    clone.setAttribute('aria-hidden', 'true');
    clone.querySelectorAll('a, button').forEach((control) => control.setAttribute('tabindex', '-1'));
    return clone;
  }

  visibleSlides() {
    const columns = getComputedStyle(this).getPropertyValue('--yuan-columns-current');
    return Math.max(1, Number.parseInt(columns, 10) || 1);
  }

  move(direction) {
    if (!this.hasAttribute('data-carousel-active')) return;
    this.currentIndex += direction;
    this.updateTransform();
  }

  moveFromUser(direction) {
    this.move(direction);
    this.restartAutoplay();
  }

  updateTransform() {
    const firstSlide = this.track.firstElementChild;
    if (!firstSlide) return;
    const gap = Number.parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const offset = this.currentIndex * (firstSlide.getBoundingClientRect().width + gap);
    this.track.style.transform = `translate3d(${-offset}px, 0, 0)`;
  }

  onTransitionEnd(event) {
    if (event.target !== this.track || event.propertyName !== 'transform') return;
    if (this.currentIndex >= this.originalSlideCount + this.cloneCount) {
      this.currentIndex = this.cloneCount;
      this.jumpToCurrentIndex();
    } else if (this.currentIndex < this.cloneCount) {
      this.currentIndex = this.originalSlideCount + this.currentIndex;
      this.jumpToCurrentIndex();
    }
  }

  jumpToCurrentIndex() {
    this.track.setAttribute('data-no-transition', '');
    this.updateTransform();
    requestAnimationFrame(() => this.track.removeAttribute('data-no-transition'));
  }

  startAutoplay() {
    if (!this.autoplayEnabled || this.autoplayUserPaused || document.hidden) return;
    if (!this.hasAttribute('data-carousel-active')) return;
    if (this.matches(':hover') || this.contains(document.activeElement)) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.stopAutoplay();
    const interval = Number(this.dataset.autoplayInterval) || 4000;
    this.autoplayTimer = window.setInterval(() => this.move(1), interval);
  }

  stopAutoplay() {
    window.clearInterval(this.autoplayTimer);
    this.autoplayTimer = null;
  }

  restartAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }

  toggleAutoplay() {
    this.autoplayUserPaused = !this.autoplayUserPaused;
    this.toggleAttribute('data-autoplay-paused', this.autoplayUserPaused);
    const label = this.autoplayUserPaused ? this.dataset.playLabel : this.dataset.pauseLabel;
    this.toggleButton.setAttribute('aria-label', label);
    this.toggleButton.querySelector('[data-carousel-toggle-label]').textContent = label;
    if (this.autoplayUserPaused) this.stopAutoplay();
    else this.startAutoplay();
  }
}

if (!customElements.get('yuan-product-carousel')) {
  customElements.define('yuan-product-carousel', YuanProductCarousel);
}
